import { useCallback, useEffect, useState } from 'react';

import type { IProspect } from '../../interfaces/prospects';
import type { IQuote } from '../../interfaces/quotes';
import QuotesService from '../../services/api/quotes/quotes';
import { ClientsStatus } from '../../types/clients';
import { excludeInactiveQuotes } from '../../utils/quotes/quotes';
import { useClientProspect } from '../useClientProspect/useClientProspect';
import { useUser } from '../useUser/useUser';

export interface IUseClientQuotesResult {
  prospect: IProspect | null;
  quotes: IQuote[];
  loading: boolean;
  refetch: () => void | Promise<void>;
}

export const useClientQuotes = (
  clientId: number,
  status: ClientsStatus | null,
  documentValue?: string | null
): IUseClientQuotesResult => {
  const { brokerId } = useUser();
  const isAwaitingStart = status === ClientsStatus.AwaitingStart;

  const prospectParams = isAwaitingStart ? { prospectId: clientId } : { documentValue };

  const {
    prospect,
    prospectId,
    loading: prospectLoading,
    refetch: refetchProspect
  } = useClientProspect(status ? prospectParams : {});

  const [quotes, setQuotes] = useState<IQuote[]>([]);
  const [quotesLoading, setQuotesLoading] = useState(true);

  const fetchQuotes = useCallback(async () => {
    if (!status || !brokerId || prospectId === null) {
      setQuotes([]);
      setQuotesLoading(false);
      return;
    }

    try {
      setQuotesLoading(true);
      const data = await QuotesService.getQuotes(brokerId, prospectId);
      setQuotes(excludeInactiveQuotes(data));
    } catch {
      setQuotes([]);
    } finally {
      setQuotesLoading(false);
    }
  }, [brokerId, prospectId, status]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const refetch = () => {
    refetchProspect();
    return fetchQuotes();
  };

  return { prospect, quotes, loading: prospectLoading || quotesLoading, refetch };
};
